/**
 * Pair (1:1) room slug helpers.
 *
 * A pair room's slug is `p-<base32(didLow)>-<base32(didHigh)>` where the two
 * participant DIDs are sorted lexicographically before encoding, so both
 * endpoints derive the same slug regardless of who is caller vs callee. The
 * worker (src/social/mutualSlug.js) recomputes/decodes this slug and admits
 * only the two encoded DIDs, so the encoder MUST stay byte-identical
 * (see slugCodec.ts).
 *
 * In practice the worker computes the authoritative slug in POST /call/ring and
 * hands it to both sides (caller in the response, callee in the `ring`
 * message), so neither client normally recomputes it. pairRoomSlug is kept for
 * deriving the "other party" label from a slug we're already in.
 */

import {decodeDidBase32, encodeDidBase32} from './slugCodec'

const PREFIX = 'p-'

/**
 * Deterministic 1:1 pair-room slug for two DIDs.
 */
export function pairRoomSlug(didA: string, didB: string): string {
  const [low, high] = didA < didB ? [didA, didB] : [didB, didA]
  return `${PREFIX}${encodeDidBase32(low)}-${encodeDidBase32(high)}`
}

/** True if a slug is a pair (1:1) room. */
export function isPairRoomSlug(slug: string): boolean {
  return slug.startsWith(PREFIX)
}

/**
 * Given a pair slug and our own DID, returns the other participant's DID
 * (for labeling "Ringing <them>…"), or undefined if not a pair slug / we can't
 * identify the peer.
 */
export function otherPairDid(slug: string, selfDid: string): string | undefined {
  if (!slug.startsWith(PREFIX)) return undefined
  const parts = slug.slice(PREFIX.length).split('-')
  if (parts.length !== 2) return undefined
  const a = decodeDidBase32(parts[0])
  const b = decodeDidBase32(parts[1])
  if (!a || !b) return undefined
  if (selfDid === a) return b
  if (selfDid === b) return a
  return undefined
}
